import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@/shared/api';
import { cn } from '@/shared/lib';
import { Button } from './Button';
import { getErrorMessage } from './errorMessage';

export interface ErrorStateProps {
  title: ReactNode;
  error: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  /** Своё действие вместо «Повторить» (например, «Сбросить период» при отказе в параметрах). */
  action?: ReactNode;
  className?: string;
}

/** Ошибка загрузки с повтором — в стиле пустого состояния (README §3), с кодом запроса для поддержки. */
export function ErrorState({
  title,
  error,
  onRetry,
  retrying = false,
  action,
  className,
}: ErrorStateProps) {
  const { t } = useTranslation(['common', 'errors']);
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center gap-6 px-24 py-40 text-center', className)}
    >
      <span className="font-display text-[20px] leading-[24px] text-ink-700">{title}</span>
      <span className="max-w-560 text-[12px] leading-[18px] text-ink-500">
        {getErrorMessage(error)}
      </span>
      {requestId ? (
        <span className="text-[11px] text-ink-400 tabular">
          {t('errors:requestId', { id: requestId })}
        </span>
      ) : null}
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-8" onClick={onRetry} loading={retrying}>
          {t('actions.retry')}
        </Button>
      ) : null}
      {action ? <div className="mt-8">{action}</div> : null}
    </div>
  );
}
